import { gql, gqlFetch } from '@/service/gql'
import type { ResultOf } from 'gql.tada'
import type { SanitizedGql } from '@/util/gqlUtil'
import type { Maybe } from '@amazon/vinyl-util'
import { getAuth } from '@/auth/authRef'
import { albumFieldsFrag } from '@/album/albumFieldsFrag'
import { artistFieldsFrag } from '@/artist/artistFieldsFrag'
import { playlistFieldsFrag } from '@/playlistdetail/playlistFieldsFrag'
import { podcastEpisodeFieldsFrag } from '@/podcast/podcastEpisodeFieldsFrag'
import { podcastShowFieldsFrag } from '@/podcast/podcastShowFieldsFrag'
import { stationFieldsFrag } from '@/service/fragments/stationFieldsFrag'
import { trackFieldsFrag } from '@/track/trackFieldsFrag'
import { videoFieldsFrag } from '@/video/videoFieldsFrag'
import { audiobookFieldsFrag } from '@/audiobook/audiobookFieldsFrag'

/**
 * Extracts the widget id portion of a firefly recommendation node id, which
 * is used as the path parameter on the see-all URL.
 *
 * Mirrors the parsing in green hornet's `RecommendationWidget.tsx`:
 *   - For music recommenders the id has the shape
 *     `uri:///recommendations/<context>/<widgetId>`. After splitting on `/`
 *     this becomes `['uri:', '', '', 'recommendations', '<context>', '<widgetId>']`,
 *     so `parts[5]` is the widget id.
 *   - For podcast recommenders the id is a single token (e.g.
 *     `dmp-podcast-ids-list-strategy-<uuid>#EDITORIAL#PODCASTS`); we return
 *     the whole thing.
 */
export function widgetIdFromRecommendationId(id: Maybe<string>): string | null {
    if (!id) return null
    const parts = id.split('/')
    if (parts.length === 1) return parts[0]
    return parts.length >= 6 ? parts[5] : null
}

/**
 * Rebuilds the firefly recommender URI from a widget id, matching what
 * green hornet's `RecommenderSeeMore` page does. Only widgets registered
 * under the `browse-home` experience resolve via the `recommender(id: ...)`
 * resolver — even if the source recommendation came from a different
 * `recommendationCollection(type: ...)` call (e.g. `music-home`).
 */
const SEE_ALL_RESOLVER_EXPERIENCE_ID = 'browse-home'

export function recommenderUriFromWidgetId(widgetId: string): string {
    return `uri:///recommendations/${SEE_ALL_RESOLVER_EXPERIENCE_ID}/${widgetId}`
}

// language=GraphQL
const seeAllQuery = gql(
    `
  query seeAll(
    $id: String!
    $limit: Float!
    $cursor: String
    $isAuthenticated: Boolean!
  ) {
    recommender: recommender(id: $id, limit: $limit, cursor: $cursor) {
      edges {
        node {
          id
          name
          itemType
          recommendedContent {
            edges {
              node {
                __typename
                ...albumFields
                ...artistFields
                ...playlistFields
                ...podcastShowFields
                ...podcastEpisodeFields
                ...stationFields
                ...trackFields
                ...videoFields
                ...audiobookFields
                # Inline mediaType on Track so we can spot music videos
                # whose primary entity is a Track (recommender returns
                # them with __typename: Track, but mediaType: VIDEO).
                # Adding this here doesn't touch trackFields' hash.
                ... on Track {
                  mediaType
                }
              }
            }
            pageInfo {
              hasNextPage
              token
            }
          }
        }
      }
    }
  }
`,
    [
        albumFieldsFrag,
        artistFieldsFrag,
        playlistFieldsFrag,
        podcastShowFieldsFrag,
        podcastEpisodeFieldsFrag,
        stationFieldsFrag,
        trackFieldsFrag,
        videoFieldsFrag,
        audiobookFieldsFrag,
    ]
)

export type SeeAllResult = SanitizedGql<ResultOf<typeof seeAllQuery>>

export type SeeAllOptions = {
    readonly id: string
    readonly limit?: Maybe<number>
    readonly cursor?: Maybe<string>
}

export async function fetchSeeAll(options: SeeAllOptions): Promise<SeeAllResult> {
    return gqlFetch(
        seeAllQuery,
        {
            id: options.id,
            limit: options.limit ?? 20,
            cursor: options.cursor ?? null,
            isAuthenticated: getAuth().isSignedIn(),
        },
        {
            cache: true,
            isRetryableError: () => true,
        }
    )
}
